import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { EmailIcon, ExclamationMarkIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/tooltip'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { getHowDidYouHearAboutUs } from '../../../api/how-did-you-hear-about-us/get-how-did-you-hear-about-us'
import { EmailValidityStatusEnum } from '../../../lib/types/enums/information-requests/email-validity-status.enum'
import { InformationRequestInput } from '../../../lib/types/models/information-request/information-request-input'
import { getSelectedOption } from '../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import { ControlledAdressInput } from '../../elements/inputs/address-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'

const EmailValidityTooltip = () => (
  <TooltipProvider delayDuration={0}>
    <Tooltip>
      <TooltipTrigger type="button">
        <ExclamationMarkIcon
          width="14px"
          height="14px"
          fill={tailwindColors.red.DEFAULT}
          color={tailwindColors.white.DEFAULT}
        />
      </TooltipTrigger>
      <TooltipContent>
        <div className="flex items-center flex-col bg-red-light p-1 rounded-sm relative">
          <EmailIcon color={tailwindColors.red.DEFAULT} />
          <Typography variant="small">Email invalide</Typography>
        </div>

        <div
          className={cn(
            'absolute bg-red-light w-2 h-2 transform rotate-45 -bottom-1 left-1/2 -translate-x-1 z-10 rounded-[2px]'
          )}
        />
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

type Props = {
  emailValidityStatus?: EmailValidityStatusEnum
  required?: boolean
  hideAddressFields?: boolean
}

const InformationRequestContactForm = ({
  emailValidityStatus,
  required,
  hideAddressFields
}: Props) => {
  const form = useFormContext<InformationRequestInput>()

  const { data: howDidYouHearAboutUsOptions } = useQuery({
    queryKey: ['how-did-you-hear-about-us'],
    queryFn: () => getHowDidYouHearAboutUs()
  })

  return (
    <InnerBox title={'Coordonnées'}>
      <div className={'flex flex-col gap-3'}>
        {!hideAddressFields && (
          <>
            <FormField
              control={form.control}
              name={'address1'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledAdressInput
                      {...field}
                      value={field.value ?? undefined}
                      label={'Adresse'}
                      required={required}
                      onDetailsChange={(placeDetails) => {
                        form.setValue('address1', placeDetails.address, { shouldValidate: true })
                        form.setValue('postalCode', placeDetails.postalCode, {
                          shouldValidate: true
                        })
                        form.setValue('city', placeDetails.city, { shouldValidate: true })
                        form.setValue('country', placeDetails.country, { shouldValidate: true })
                        form.trigger(['address1', 'postalCode', 'city', 'country'])
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="address2"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      value={field.value ?? undefined}
                      label="Complément d'adresse"
                      type="text"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex gap-2">
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledInput
                        {...field}
                        value={field.value ?? undefined}
                        label="Code Postal"
                        type="text"
                        required={required}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <ControlledInput
                        {...field}
                        value={field.value ?? undefined}
                        label="Ville"
                        type="text"
                        required={required}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      value={field.value ?? undefined}
                      label="Pays"
                      type="text"
                      required={required}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledPhoneInput
                      {...field}
                      value={field.value ?? undefined}
                      label="Numéro de téléphone"
                      defaultCountry="FR"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </>
        )}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledInput
                  {...field}
                  label="Email"
                  type="email"
                  required={required}
                  className={cn({
                    'text-red': emailValidityStatus === EmailValidityStatusEnum.INVALID
                  })}
                  icon={conditionalRender(
                    emailValidityStatus === EmailValidityStatusEnum.INVALID,
                    () => (
                      <EmailValidityTooltip />
                    )
                  )}
                  iconToTheRight
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="howDidYouHearAboutUsOptionId"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  required
                  label="Comment avez-vous connu LEC ?"
                  options={howDidYouHearAboutUsOptions?.data.map((option) => ({
                    label: option.name,
                    value: option.id
                  }))}
                  selectedOption={getSelectedOption(
                    field.value,
                    howDidYouHearAboutUsOptions?.data.find((option) => option.id === field.value)
                      ?.name || ''
                  )}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </InnerBox>
  )
}

export default InformationRequestContactForm
